import { SLOTS, UPGRADE_BRANCHES, type Content, type Slot, type UpgradeDef } from '@gra/content';
import {
  achievementProgress,
  activeTitle,
  bannerFeatured,
  bannerRotationLeft,
  shardUpgradeCost,
  unclaimedAchievements,
  branchPoints,
  canPull,
  isUpgradeUnlocked,
  spentUpgradeRanks,
  upgradeModifiers,
  upgradeRankCost,
  upgradeVisible,
  computeAbilityStats,
  computePlayerStats,
  currentLoadout,
  equipmentModifiers,
  equippedItems,
  equipmentPower,
  powerPenalty,
  findItem,
  itemModifiers,
  itemPower,
  upgradeCost,
  type ItemInstance,
  type Profile,
  type PullResult,
} from '@gra/engine';
import type { Modifier } from '@gra/content';
import { sfx } from '../audio';
import type { IconRenderer } from '../three/icons';
import { armorModel } from '../three/visuals';
import { abilityIcon, armorIcon, artImage, classIcon, raceIcon, statIcon, treeIcon } from './ability-icons';
import { glyph, glyphChip } from './glyphs';
import { settingsButton } from './settings-ui';
import { el, formatTime } from './dom';
import { describeModifier, isGoodModifier, SLOT_LABELS } from './format';

export interface HubActions {
  equip(uid: number): void;
  unequip(slot: Slot): void;
  setClass(classId: string): void;
  setRace(raceId: string, gender: 'm' | 'k'): void;
  pull(count: number, bannerId: string): void;
  /** Sklepik banera: wyróżniony przedmiot za odłamki. */
  buyFeatured(bannerId: string, itemId: string): void;
  /** Wzmocnienie przedmiotu odłamkami (+1 poziom). */
  upgradeShards(uid: number): void;
  claimAchievement(id: string): void;
  setTitle(id: string | null): void;
  setFrame(id: string | null): void;
  /** Zakup rangi ulepszenia z drzewka rozwoju. */
  buyUpgrade(id: string): void;
  /** Wzmocnienie przedmiotu złotem (+1 poziom). */
  upgrade(uid: number): void;
  startRun(zoneId: string): void;
}

/** Okna otwierane z zakładek i przycisków pod bohaterem (jedno naraz). */
type Section = 'items' | 'growth' | 'summon' | 'achievements';

const BRANCH_NAMES: Record<string, string> = { atak: 'Atak', obrona: 'Obrona', fortuna: 'Fortuna', klasa: 'Klasa' };

const TABS: { id: Section; label: string }[] = [
  { id: 'items', label: 'Ekwipunek' },
  { id: 'growth', label: 'Talenty' },
  { id: 'summon', label: 'Przywołanie' },
  { id: 'achievements', label: 'Osiągnięcia' },
];

/**
 * Ekran obozu w trzech kolumnach: klasa (lewa), bohater z ekwipunkiem na scenie 3D (środek),
 * wybór strefy z harmonogramem bossów (prawa). Pod tytułem zakładki otwierające okna
 * (ekwipunek, rozwój, przywołanie), na dole duży przycisk wyprawy do wybranej strefy.
 */
export class HubUi {
  private readonly root = el('div', 'hub');
  private readonly results = el('div', 'screen gacha-results');
  private readonly tooltip = el('div', 'tooltip frame');
  /** Karta, której podpowiedź otwarto dotykiem: pierwsze stuknięcie pokazuje statystyki, drugie dopiero zakłada. */
  private touchOwner: HTMLElement | null = null;
  private lastPointer = 'mouse';
  private filter: Slot | 'all' = 'all';
  /** Otwarte okno (jedno naraz) albo null. */
  private section: Section | null = null;
  /** Wybrany talent w oknie drzewka (panel szczegółów). */
  private selectedUpgrade: string | null = null;
  /** Wybrana strefa wyprawy. */
  private zoneId: string;
  private bannerId: string;
  private profile: Profile | null = null;

  constructor(
    private readonly content: Content,
    private readonly icons: IconRenderer,
    private readonly actions: HubActions,
  ) {
    this.zoneId = Object.keys(content.zones)[0]!;
    // Domyślnie baner wyróżniony (jeśli jest), bo ma sklepik i zestaw tygodnia.
    this.bannerId = Object.keys(content.banners)[1] ?? Object.keys(content.banners)[0]!;
    this.root.hidden = true;
    this.results.hidden = true;
    this.tooltip.hidden = true;
    document.getElementById('ui')!.append(this.root, this.results, this.tooltip);
    window.addEventListener('keydown', (e) => {
      if (e.code === 'Escape' && this.section && !this.root.hidden) this.open(null);
    });
    // Podpowiedź otwarta dotykiem znika przy następnym stuknięciu gdziekolwiek.
    window.addEventListener(
      'pointerdown',
      (e) => {
        this.lastPointer = e.pointerType;
        if (this.touchOwner && !this.touchOwner.contains(e.target as Node)) {
          this.tooltip.hidden = true;
          this.touchOwner = null;
        }
      },
      true,
    );
    window.addEventListener('pointermove', (e) => {
      if (this.tooltip.hidden) return;
      const x = Math.min(e.clientX + 18, window.innerWidth - this.tooltip.offsetWidth - 8);
      const y = Math.min(e.clientY + 18, window.innerHeight - this.tooltip.offsetHeight - 8);
      this.tooltip.style.transform = `translate(${x}px, ${y}px)`;
    });
  }

  show(profile: Profile): void {
    this.root.hidden = false;
    this.render(profile);
  }

  hide(): void {
    this.root.hidden = true;
    this.results.hidden = true;
    this.tooltip.hidden = true;
  }

  /**
   * Podpowiedź po najechaniu, a na dotyku po pierwszym stuknięciu (drugie stuknięcie dopiero wykonuje akcję).
   * Dzięki temu opisy nie muszą stać na ekranie jako tekst.
   */
  private bindTip(node: HTMLElement, build: () => Node[]): void {
    node.addEventListener('pointerenter', (e) => {
      this.tooltip.replaceChildren(...build());
      this.tooltip.hidden = false;
      // Dotyk nie wysyła pointermove nad elementem: podpowiedź staje obok i zostaje do następnego stuknięcia.
      if (e.pointerType === 'touch') {
        const r = node.getBoundingClientRect();
        const x = Math.max(8, Math.min(r.right + 10, window.innerWidth - this.tooltip.offsetWidth - 8));
        const y = Math.max(8, Math.min(r.top, window.innerHeight - this.tooltip.offsetHeight - 8));
        this.tooltip.style.transform = `translate(${x}px, ${y}px)`;
      }
    });
    node.addEventListener(
      'click',
      (e) => {
        if (this.lastPointer !== 'touch') return;
        if (this.touchOwner === node) {
          this.touchOwner = null;
          return;
        }
        e.stopImmediatePropagation();
        this.touchOwner = node;
      },
      true,
    );
    node.addEventListener('pointerleave', (e) => {
      if (e.pointerType !== 'touch') this.tooltip.hidden = true;
    });
  }

  /** Prosta podpowiedź: nagłówek i linie tekstu. */
  private textTip(node: HTMLElement, title: string, ...lines: string[]): void {
    this.bindTip(node, () => [el('div', 'tt-name', title), ...lines.filter(Boolean).map((l) => el('div', 'tt-line', l))]);
  }

  /** Podpowiedź przedmiotu w klasycznym stylu RPG: nazwa w kolorze rzadkości, slot, statystyki, opis. */
  private bindTooltip(node: HTMLElement, inst: ItemInstance): void {
    this.bindTip(node, () => {
      const def = this.content.items[inst.itemId]!;
      const rarity = this.content.rarities[inst.rarity]!;
      const name = el('div', 'tt-name', def.name + (inst.level > 1 ? ` +${inst.level - 1}` : ''));
      name.style.color = rarity.color;
      const stats = el('div', 'tt-stats');
      for (const m of itemModifiers(this.content, inst)) stats.append(el('div', isGoodModifier(m) ? 'up' : 'down', describeModifier(m)));
      const out: Node[] = [
        name,
        el('div', 'tt-meta', `${rarity.name} · ${SLOT_LABELS[def.slot]}${def.twoHanded ? ', dwuręczna' : ''}`),
        stats,
        el('div', 'tt-flavor', `„${def.description}”`),
        el('div', 'tt-meta', `Moc przedmiotu: ${itemPower(this.content, inst)}`),
      ];
      const diff = this.comparison(inst);
      if (diff) out.push(diff);
      return out;
    });
  }

  /**
   * Porównanie z przedmiotem założonym w tym samym slocie: co zyskasz, a co stracisz po zamianie.
   * Mniejszy czas odnowienia to zysk, więc dla niego znak „dobrego” jest odwrócony.
   */
  private comparison(inst: ItemInstance): HTMLElement | null {
    if (!this.profile) return null;
    const def = this.content.items[inst.itemId]!;
    const uid = currentLoadout(this.profile)[def.slot];
    if (uid === undefined || uid === inst.uid) return null;
    const other = findItem(this.profile, uid);
    if (!other) return null;
    const sum = (mods: Modifier[]) => {
      const out = new Map<string, Modifier>();
      for (const m of mods) {
        const key = `${m.stat}|${m.op}|${(m.tags ?? []).join(',')}`;
        const prev = out.get(key);
        out.set(key, prev ? { ...prev, value: prev.value + m.value } : { ...m });
      }
      return out;
    };
    const mine = sum(itemModifiers(this.content, inst));
    const theirs = sum(itemModifiers(this.content, other));
    const box = el('div', 'tt-compare');
    box.append(el('div', 'tt-meta', `Zamiast: ${this.content.items[other.itemId]!.name}`));
    let any = false;
    for (const key of new Set([...mine.keys(), ...theirs.keys()])) {
      const base = mine.get(key) ?? theirs.get(key)!;
      const value = (mine.get(key)?.value ?? 0) - (theirs.get(key)?.value ?? 0);
      if (Math.abs(value) < 1e-6) continue;
      any = true;
      const good = isGoodModifier({ ...base, value });
      box.append(el('div', good ? 'up' : 'down', `${good ? '▲' : '▼'} ${describeModifier({ ...base, value })}`));
    }
    if (!any) box.append(el('div', 'tt-meta', 'Bez zmian w statystykach'));
    return box;
  }

  render(profile: Profile): void {
    this.profile = profile;
    this.tooltip.hidden = true;
    const top = el('header', 'topbar');
    const right = el('div', 'top-right');
    right.append(this.currency(profile), settingsButton());
    top.append(right);

    const title = el('div', 'hub-title');
    title.append(el('h1', 'brand', 'Echa Pustkowi'), el('div', 'hub-sub', 'Obóz u skraju pustkowi'));

    const tabs = el('nav', 'hub-tabs');
    for (const t of TABS) {
      const b = el('button', t.id === this.section ? 'hub-tab active' : 'hub-tab', t.label);
      const waiting = t.id === 'achievements' ? unclaimedAchievements(profile) : 0;
      if (waiting > 0) b.append(el('span', 'tab-badge', String(waiting)));
      b.addEventListener('click', () => this.open(this.section === t.id ? null : t.id));
      tabs.append(b);
    }

    const nodes: HTMLElement[] = [top, title, tabs, this.classColumn(profile), this.heroColumn(profile), this.zoneColumn(), this.runBar()];
    if (this.section) {
      const panels: Record<Section, () => HTMLElement> = {
        items: () => this.inventoryPanel(profile),
        summon: () => this.summonPanel(profile),
        growth: () => this.growthPanel(profile),
        achievements: () => this.achievementsPanel(profile),
      };
      const overlay = el('div', 'hub-modal');
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) this.open(null);
      });
      overlay.append(panels[this.section]());
      nodes.push(overlay);
    }
    this.root.replaceChildren(...nodes);
  }

  private open(section: Section | null): void {
    sfx.click();
    this.section = section;
    if (this.profile) this.render(this.profile);
  }

  /** Nagłówek kolumny z ozdobnymi liniami po bokach. */
  private heading(text: string): HTMLElement {
    const h = el('h2', 'ornament');
    h.append(el('span', '', text));
    return h;
  }

  /** Lewa kolumna: wybór klasy (ikony), opis, statystyki i umiejętności klasy. */
  private classColumn(profile: Profile): HTMLElement {
    const col = el('section', 'hub-col col-class');
    const cls = this.content.classes[profile.classId]!;
    const grid = el('div', 'class-grid');
    for (const c of Object.values(this.content.classes)) {
      const b = el('button', c.id === profile.classId ? 'class-tile active' : 'class-tile');
      const img = el('img');
      img.src = classIcon(c.id, this.content.abilities[c.startingAbilities[0]!]!);
      b.append(img, el('span', '', c.name));
      b.style.setProperty('--accent', c.color);
      b.addEventListener('click', () => this.actions.setClass(c.id));
      this.textTip(b, c.name, c.description);
      grid.append(b);
    }

    // Statystyki jak na wyprawie: ekwipunek + drzewko rozwoju.
    // Te same źródła co na wyprawie: ekwipunek, drzewko talentów i cechy rasy.
    const raceMods = this.content.races[profile.raceId]?.modifiers ?? [];
    const mods = [...equipmentModifiers(this.content, profile), ...upgradeModifiers(this.content, profile), ...raceMods];
    const s = computePlayerStats(cls.baseStats, mods);
    // Statystyki jako ikona + liczba; nazwa i objaśnienie w podpowiedzi.
    const stats = el('div', 'stat-boxes');
    const box = (stat: string, label: string, value: string, hint: string) => {
      const b = el('div', 'stat-box');
      const img = el('img', 'stat-ico');
      img.src = statIcon(stat);
      img.alt = label;
      b.append(img, el('div', 'stat-value', value));
      this.textTip(b, label, hint);
      stats.append(b);
    };
    box('maxHealth', 'Zdrowie', Math.round(s.maxHealth).toString(), 'Maksymalne zdrowie na wyprawie.');
    // Pancerz zmniejsza obrażenia o armor / (100 + armor), jak w silniku.
    const armorCut = Math.round((s.armor / (100 + s.armor)) * 100);
    box('armor', 'Pancerz', Math.round(s.armor).toString(), `Zmniejsza otrzymywane obrażenia o ${armorCut}%.`);
    box('moveSpeed', 'Szybkość', Math.round(s.moveSpeed).toString(), 'Szybkość ruchu bohatera.');
    box('critChance', 'Krytyk', `${Math.round(s.critChance * 100)}%`, 'Szansa na trafienie krytyczne (podwójne obrażenia).');

    // Umiejętności klasy: startowa i te z talentów (z ewolucjami w podpowiedzi).
    const ids = new Set(cls.startingAbilities);
    for (const t of Object.values(this.content.talents)) if (t.grantsAbility && t.classes.includes(cls.id)) ids.add(t.grantsAbility);
    const pool = el('div', 'ability-pool');
    for (const id of ids) {
      const a = this.content.abilities[id];
      if (!a) continue;
      const img = el('img');
      img.src = abilityIcon(a);
      const evo = a.evolution ? this.content.abilities[a.evolution] : undefined;
      const st = computeAbilityStats(a, mods);
      const tile = el('span', 'pool-ico');
      tile.append(img);
      this.textTip(tile, a.name, a.description, `${Math.round(st.damage)} obr. co ${st.cooldown.toFixed(2)} s`, evo ? `Ewolucja na randze V: ${evo.name}` : '');
      pool.append(tile);
    }

    // Rasa i płeć: wygląd bohatera i cechy rasowe (działają na każdej wyprawie).
    const race = this.content.races[profile.raceId] ?? Object.values(this.content.races)[0]!;
    const races = el('div', 'race-grid');
    for (const r of Object.values(this.content.races)) {
      const b = el('button', r.id === race.id ? 'race-tile active' : 'race-tile');
      const icon = raceIcon(r.id);
      if (icon) {
        const img = el('img');
        img.src = icon;
        b.append(img);
      } else b.append(el('span', 'race-letter', r.name[0] ?? '?'));
      b.addEventListener('click', () => this.actions.setRace(r.id, profile.gender));
      this.textTip(b, r.name, ...r.traits.map((t) => `${t.name}: ${t.description}`));
      races.append(b);
    }
    const genders = el('div', 'gender-toggle');
    for (const [g, label] of [['m', '♂'], ['k', '♀']] as const) {
      const b = el('button', profile.gender === g ? 'hub-tab gender active' : 'hub-tab gender', label);
      b.setAttribute('aria-label', g === 'm' ? 'Mężczyzna' : 'Kobieta');
      b.addEventListener('click', () => this.actions.setRace(race.id, g));
      genders.append(b);
    }
    // Cechy rasy jako krótkie pastylki; pełny opis w podpowiedzi.
    const traits = el('div', 'race-traits');
    for (const t of race.traits) {
      const chip = el('span', 'trait-chip', t.name);
      this.textTip(chip, t.name, t.description);
      traits.append(chip);
    }
    const raceHead = el('div', 'race-head');
    raceHead.append(el('span', 'class-name', race.name), genders);

    col.append(
      this.heading('Rasa'),
      races,
      raceHead,
      traits,
      this.heading('Klasa'),
      grid,
      stats,
      pool,
    );
    return col;
  }

  /** Środek: bohater na scenie 3D (przezroczyste okno), pod nim założony sprzęt i skróty do okien. */
  private heroColumn(profile: Profile): HTMLElement {
    const col = el('section', 'hub-col col-hero');
    const cls = this.content.classes[profile.classId]!;
    const power = equippedItems(this.content, profile).reduce((sum, { inst }) => sum + itemPower(this.content, inst), 0);
    const head = el('div', 'hero-head');
    head.append(el('div', 'hero-name', cls.name));
    const title = activeTitle(this.content, profile);
    if (title) head.append(el('div', 'hero-title', `„${title}”`));
    const powerChip = glyphChip('power', String(power), 'hero-power');
    this.textTip(powerChip, 'Moc ekwipunku', 'Suma mocy założonych przedmiotów. Trudniejsze strefy mają zalecaną moc.');
    head.append(powerChip);

    const slots = el('div', 'hero-slots');
    const equipped = new Map(equippedItems(this.content, profile).map((e) => [e.slot, e]));
    for (const slot of SLOTS) {
      const e = equipped.get(slot);
      const card = el('button', e ? 'hero-slot filled' : 'hero-slot');
      if (e) {
        card.style.setProperty('--rarity', this.content.rarities[e.inst.rarity]!.color);
        card.append(this.icon(e.inst));
        card.addEventListener('click', () => this.open('items'));
        this.bindTooltip(card, e.inst);
      } else {
        card.append(el('span', 'slot-empty', '+'));
        this.textTip(card, SLOT_LABELS[slot], 'Pusty slot');
        card.addEventListener('click', () => this.open('items'));
      }
      slots.append(card);
    }
    const forClass = profile.inventory.filter((i) => this.content.items[i.itemId]?.classes.includes(profile.classId)).length;
    const buttons = el('div', 'hero-buttons');
    // Skróty pod bohaterem: symbol + nazwa + licznik w znaczku zamiast zdania.
    const btn = (icon: 'bag' | 'tree', label: string, count: number, section: Section) => {
      const b = el('button', 'hero-btn');
      b.append(glyph(icon), el('span', 'hero-btn-label', label), el('span', 'hero-btn-count', String(count)));
      b.addEventListener('click', () => this.open(section));
      buttons.append(b);
    };
    btn('bag', 'Ekwipunek', forClass, 'items');
    btn('tree', 'Talenty', spentUpgradeRanks(profile), 'growth');
    col.append(head, el('div', 'hero-stage'), slots, buttons);
    return col;
  }

  /** Prawa kolumna: strefy wypraw z wrogami i harmonogramem bossów; kliknięcie wybiera strefę. */
  private zoneColumn(): HTMLElement {
    const col = el('section', 'hub-col col-zone');
    const list = el('div', 'zone-list');
    const power = this.profile ? equipmentPower(this.content, this.profile) : 0;
    for (const zone of Object.values(this.content.zones)) {
      const card = el('button', `zone-tile zone-${zone.visual}${zone.id === this.zoneId ? ' active' : ''}`);
      const art = artImage(`zone-${zone.id}`);
      if (art) card.style.setProperty('--art', `url(${art})`);
      const head = el('div', 'zone-head');
      head.append(el('span', 'zone-title', zone.name), el('span', 'skulls', '☠'.repeat(zone.difficulty)));
      // Zamiast akapitów: rząd symboli z liczbami, a nazwy i opis w podpowiedzi strefy.
      const enemies = zone.enemies.map((e) => this.content.enemies[e.enemy]?.name).filter(Boolean) as string[];
      const facts = el('div', 'zone-facts');
      facts.append(glyphChip('skull', String(enemies.length)));
      for (const b of zone.bosses) facts.append(glyphChip('crown', formatTime(b.at), b.final ? 'boss final' : 'boss'));
      facts.append(glyphChip('chest', `×${zone.rewardMultiplier}`, 'rew'), glyphChip('heart', `×${zone.healthMultiplier}`, 'hp'));
      card.append(head, facts);
      // Zalecana moc ekwipunku: przy słabszym sprzęcie wrogowie mają więcej zdrowia.
      const rec = zone.recommendedPower;
      const extra = rec > 0 ? Math.round((powerPenalty(this.content, zone.id, power) - 1) * 100) : 0;
      if (rec > 0) card.append(glyphChip('power', extra > 0 ? `${power} / ${rec}` : `${rec} ✓`, extra > 0 ? 'zone-power low' : 'zone-power ok'));
      this.textTip(
        card,
        zone.name,
        zone.description,
        `Wrogowie: ${enemies.join(', ')}`,
        `Bossowie: ${zone.bosses.map((b) => `${formatTime(b.at)} ${this.content.enemies[b.enemy]?.name ?? ''}`).join(' · ')}`,
        `Nagrody ×${zone.rewardMultiplier} · zdrowie wrogów ×${zone.healthMultiplier}`,
        rec > 0 ? (extra > 0 ? `Zalecana moc ${rec} (masz ${power}): wrogowie +${extra}% zdrowia` : `Zalecana moc ${rec}: masz wystarczająco`) : '',
      );
      card.addEventListener('click', () => {
        this.zoneId = zone.id;
        sfx.click();
        if (this.profile) this.render(this.profile);
      });
      list.append(card);
    }
    col.append(this.heading('Strefa'), list);
    return col;
  }

  /** Dół: duży przycisk wyprawy do wybranej strefy i podpowiedź sterowania. */
  private runBar(): HTMLElement {
    const bar = el('footer', 'run-bar');
    const zone = this.content.zones[this.zoneId]!;
    const final = zone.bosses.find((b) => b.final);
    const run = el('button', 'run-btn');
    const sub = el('span', 'run-sub');
    if (final) sub.append(glyphChip('crown', `${this.content.enemies[final.enemy]?.name ?? 'Boss'} · ${formatTime(final.at)}`));
    run.append(el('span', 'run-label', `Wyrusz: ${zone.name}`), sub);
    run.addEventListener('click', () => this.actions.startRun(this.zoneId));
    bar.append(run);
    return bar;
  }

  private currency(profile: Profile): HTMLElement {
    // Waluty: symbol + liczba; co to jest, mówi podpowiedź.
    const node = el('div', 'currency');
    node.append(el('span', 'gem', '◆'), el('b', '', profile.currency.toLocaleString('pl-PL')));
    this.textTip(node, 'Kryształy', 'Waluta Przywołania. Za wyprawy, zabójstwa i osiągnięcia.');
    const gold = el('div', 'currency gold');
    gold.append(el('span', 'coin'), el('b', '', profile.gold.toLocaleString('pl-PL')));
    this.textTip(gold, 'Złoto', 'Ze skrzyń i potworów. Wzmacnia przedmioty i kupuje talenty.');
    const shards = el('div', 'currency shards');
    shards.append(el('span', 'shard', '◇'), el('b', '', profile.shards.toLocaleString('pl-PL')));
    this.textTip(shards, 'Odłamki', 'Z duplikatów. Wzmacniają przedmioty i kupują wyróżniony sprzęt.');
    const wrap = el('div', 'currencies');
    wrap.append(node, gold, shards);
    return wrap;
  }

  private icon(inst: ItemInstance): HTMLImageElement {
    const def = this.content.items[inst.itemId]!;
    const rarity = this.content.rarities[inst.rarity]!;
    // Zbroja z modelem części stroju: zdjęcie tej samej części w kolorze przedmiotu (jak na postaci).
    const outfit = def.look && this.profile ? armorModel(def.id, this.profile.gender) : undefined;
    if (outfit && def.look && this.profile) {
      const img = el('img', 'item-icon');
      const race = this.content.races[this.profile.raceId]!.look;
      img.src = this.icons.armor(outfit, { ...def.look, tier: this.content.rarityOrder.indexOf(inst.rarity), rarity: rarity.color }, race.skinTone);
      img.style.setProperty('--rarity', rarity.color);
      img.alt = def.name;
      return img;
    }
    // Zdjęcie modelu 3D przedmiotu (jak na postaci); malowany rysunek tylko dla zbroi bez modelu.
    // Obrazki z AI miały inny styl niż zdjęcia modeli — ikony wyglądały na zbieraninę.
    const painted = def.look ? armorIcon(def, rarity.color) : null;
    const img = el('img', painted ? 'item-icon painted' : 'item-icon');
    img.src = painted ?? this.icons.icon(def.visual, rarity.color, this.content.rarityOrder.indexOf(inst.rarity));
    img.style.setProperty('--rarity', rarity.color);
    img.alt = def.name;
    return img;
  }

/** Wspólna oprawa panelu: tytuł, opcjonalny podpis i przycisk zamknięcia. */
  private panel(title: string, subtitle?: string): HTMLElement {
    const panel = el('aside', 'panel modal-panel');
    const head = el('header', 'panel-head');
    const titles = el('div');
    titles.append(el('h2', '', title));
    if (subtitle) titles.append(el('div', 'panel-sub', subtitle));
    const close = el('button', 'close-btn', '×');
    close.title = 'Zamknij';
    close.addEventListener('click', () => this.open(null));
    head.append(titles, close);
    panel.append(head);
    return panel;
  }

  private inventoryPanel(profile: Profile): HTMLElement {
    const panel = this.panel('Ekwipunek');
    const equippedUids = new Set(Object.values(currentLoadout(profile)));

    const filters = el('div', 'filters');
    for (const f of ['all', ...SLOTS] as const) {
      const b = el('button', f === this.filter ? 'chip active' : 'chip', f === 'all' ? 'Wszystko' : SLOT_LABELS[f]);
      b.addEventListener('click', () => {
        this.filter = f;
        if (this.profile) this.render(this.profile);
      });
      filters.append(b);
    }

    // Pokazujemy tylko sprzęt aktualnej klasy — reszta czeka na swojego bohatera.
    const forClass = profile.inventory.filter((i) => this.content.items[i.itemId]?.classes.includes(profile.classId));
    const items = forClass
      .filter((i) => this.filter === 'all' || this.content.items[i.itemId]?.slot === this.filter)
      .sort((x, y) => itemPower(this.content, y) - itemPower(this.content, x));

    const list = el('div', 'inventory');
    for (const inst of items) {
      const def = this.content.items[inst.itemId]!;
      const rarity = this.content.rarities[inst.rarity]!;
      const card = el('button', 'item-card');
      card.style.setProperty('--rarity', rarity.color);
      if (equippedUids.has(inst.uid)) card.classList.add('equipped');
      const info = el('div', 'item-info');
      const name = el('div', 'item-name', def.name);
      if (inst.level > 1) name.append(el('span', 'level', ` +${inst.level - 1}`));
      info.append(
        name,
        el('div', 'item-meta', `${rarity.name} · ${SLOT_LABELS[def.slot]}${def.twoHanded ? ' (2H)' : ''}`),
        el('div', 'item-mods', itemModifiers(this.content, inst).map(describeModifier).join(' · ')),
      );
      card.append(this.icon(inst), info);
      // Wzmocnienie złotem albo odłamkami: +1 poziom przedmiotu (silniejsze statystyki).
      const cost = upgradeCost(this.content, inst);
      if (cost !== null) {
        const ups = el('div', 'upgrade-btns');
        const up = el('span', profile.gold >= cost ? 'upgrade-btn' : 'upgrade-btn disabled');
        up.setAttribute('role', 'button');
        up.title = profile.gold >= cost ? `Wzmocnij do +${inst.level} za ${cost} złota` : `Potrzeba ${cost} złota`;
        up.append(el('span', 'arrow', '▲'), el('span', 'coin'), el('span', '', String(cost)));
        up.addEventListener('click', (e) => {
          e.stopPropagation();
          if (profile.gold >= cost) this.actions.upgrade(inst.uid);
        });
        ups.append(up);
        const shardCost = shardUpgradeCost(this.content, inst);
        if (shardCost !== null && profile.shards > 0) {
          const sb = el('span', profile.shards >= shardCost ? 'upgrade-btn shard' : 'upgrade-btn shard disabled');
          sb.setAttribute('role', 'button');
          sb.title = profile.shards >= shardCost ? `Wzmocnij do +${inst.level} za ${shardCost} odłamków` : `Potrzeba ${shardCost} odłamków`;
          sb.append(el('span', 'arrow', '▲'), el('span', 'shard', '◇'), el('span', '', String(shardCost)));
          sb.addEventListener('click', (e) => {
            e.stopPropagation();
            if (profile.shards >= shardCost) this.actions.upgradeShards(inst.uid);
          });
          ups.append(sb);
        }
        card.append(ups);
      }
      card.addEventListener('click', () => this.actions.equip(inst.uid));
      this.bindTooltip(card, inst);
      list.append(card);
    }
    if (items.length === 0) list.append(el('p', 'muted', 'Pusto. Przywołaj coś!'));

    const body = el('div', 'panel-body fill');
    body.append(filters, list);
    panel.append(body);
    return panel;
  }

/**
   * Drzewko talentów: gałęzie atak, obrona, fortuna i gałąź bieżącej klasy, z węzłami-ikonami w siatce,
   * liniami do poprzedników i panelem szczegółów wybranego talentu. Talenty kupuje się złotem; kolejne rzędy
   * gałęzi odblokowują punkty wydane w tej gałęzi. Na dole każdej gałęzi czeka zwieńczenie.
   */
  private growthPanel(profile: Profile): HTMLElement {
    const panel = this.panel('Talenty');
    panel.classList.add('wide');
    const all = Object.values(this.content.upgrades).filter((u) => upgradeVisible(profile, u));
    if (!this.selectedUpgrade || !all.some((u) => u.id === this.selectedUpgrade)) this.selectedUpgrade = all[0]?.id ?? null;

    const layout = el('div', 'tree-layout');
    for (const branch of UPGRADE_BRANCHES) {
      const nodes = all.filter((u) => u.branch === branch);
      if (!nodes.length) continue;
      const points = branchPoints(this.content, profile, branch);
      const col = el('div', `tree-branch branch-${branch}`);
      if (branch === 'klasa') col.style.setProperty('--tint', `color-mix(in srgb, ${this.content.classes[profile.classId]!.color} 40%, #000)`);
      // Gałąź klasy nie ma własnej grafiki: tłem jest malowany portret klasy, jak w kafelkach wyboru klasy.
      const cls = this.content.classes[profile.classId]!;
      const art = artImage(`branch-${branch}`) ?? (branch === 'klasa' ? classIcon(cls.id, this.content.abilities[cls.startingAbilities[0]!]!) : null);
      if (art) col.style.setProperty('--art', `url(${art})`);
      const head = el('div', 'tree-head');
      const hicon = el('img');
      hicon.src = treeIcon(nodes[0]!.id, nodes[0]!.modifiers[0]!.stat, nodes[0]!.icon);
      const title = branch === 'klasa' ? this.content.classes[profile.classId]!.name : BRANCH_NAMES[branch]!;
      head.append(hicon, el('span', 'tree-title', title), el('span', 'tree-points', `${points} pkt`));

      const rows = Math.max(...nodes.map((u) => u.row)) + 1;
      const grid = el('div', 'tree-grid');
      grid.style.setProperty('--rows', String(rows));
      // Linie od poprzednika do talentu (pod węzłami); świecą, gdy poprzednik ma rangę.
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'tree-lines');
      svg.setAttribute('viewBox', `0 0 3 ${rows}`);
      svg.setAttribute('preserveAspectRatio', 'none');
      for (const u of nodes) {
        const parent = u.after ? this.content.upgrades[u.after] : undefined;
        if (!parent) continue;
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', String(parent.col + 0.5));
        line.setAttribute('y1', String(parent.row + 0.5));
        line.setAttribute('x2', String(u.col + 0.5));
        line.setAttribute('y2', String(u.row + 0.5));
        line.setAttribute('class', (profile.upgrades[parent.id] ?? 0) > 0 ? 'on' : '');
        svg.append(line);
      }
      grid.append(svg);
      for (const u of nodes) grid.append(this.treeNode(profile, u));

      const milestones = el('div', 'tree-milestones');
      // Tylko dwa najbliższe progi — przy długich gałęziach pełna lista zajmowała pół ekranu.
      const reqs = [...new Set(nodes.map((u) => u.requires))].filter((r) => r > points).sort((a, b) => a - b).slice(0, 2);
      // Progi jako kłódka + liczba punktów; który rząd otwierają, mówi podpowiedź.
      for (const req of reqs) {
        const m = glyphChip('lock', String(req), 'milestone');
        this.textTip(m, `Próg ${req} pkt`, `Odblokowuje rząd ${nodes.find((u) => u.requires === req)!.row + 1} tej gałęzi.`);
        milestones.append(m);
      }
      col.append(head, grid, milestones);
      layout.append(col);
    }
    layout.append(this.treeDetails(profile));
    panel.append(layout);
    return panel;
  }

  private treeNode(profile: Profile, u: UpgradeDef): HTMLElement {
    const rank = profile.upgrades[u.id] ?? 0;
    const unlocked = isUpgradeUnlocked(this.content, profile, u);
    const cls = ['tree-node', rank >= u.maxRank ? 'maxed' : rank > 0 ? 'partial' : unlocked ? 'open' : 'locked'];
    if (u.capstone) cls.push('capstone');
    if (u.id === this.selectedUpgrade) cls.push('selected');
    const node = el('button', cls.join(' '));
    node.style.gridRow = String(u.row + 1);
    node.style.gridColumn = String(u.col + 1);
    const img = el('img');
    img.src = treeIcon(u.id, u.modifiers[0]!.stat, u.icon);
    node.append(img, el('span', 'tree-rank', `${rank}/${u.maxRank}`));
    node.addEventListener('click', () => {
      this.selectedUpgrade = u.id;
      sfx.hover();
      this.render(profile);
    });
    // Podwójne kliknięcie od razu kupuje rangę.
    node.addEventListener('dblclick', () => {
      const cost = upgradeRankCost(profile, u);
      if (cost !== null && unlocked && profile.gold >= cost) this.actions.buyUpgrade(u.id);
    });
    return node;
  }

  /** Panel po prawej: złoto, wydane punkty i szczegóły wybranego talentu z przyciskiem nauki. */
  private treeDetails(profile: Profile): HTMLElement {
    const side = el('div', 'tree-side');
    const gold = el('div', 'growth-gold');
    gold.append(el('span', 'coin'), el('b', '', profile.gold.toLocaleString('pl-PL')), glyphChip('tree', String(spentUpgradeRanks(profile)), 'spent'));
    this.textTip(gold, 'Złoto i wydane punkty', 'Złoto zdobywasz na wyprawach: ze skrzyń, potworów i bossów.');
    side.append(gold);
    if (!this.selectedUpgrade) side.append(el('div', 'tree-pick', 'Wybierz talent'));

    const u = this.selectedUpgrade ? this.content.upgrades[this.selectedUpgrade] : undefined;
    if (!u) return side;
    const rank = profile.upgrades[u.id] ?? 0;
    const cost = upgradeRankCost(profile, u);
    const unlocked = isUpgradeUnlocked(this.content, profile, u);
    const card = el('div', 'tree-detail');
    const head = el('div', 'tree-detail-head');
    const img = el('img');
    img.src = treeIcon(u.id, u.modifiers[0]!.stat, u.icon);
    const title = el('div');
    const branchName = u.branch === 'klasa' ? this.content.classes[profile.classId]!.name : BRANCH_NAMES[u.branch];
    title.append(el('div', 'growth-name', u.name), el('div', 'muted small', `${branchName}${u.capstone ? ' · zwieńczenie' : ''} · ranga ${rank}/${u.maxRank}`));
    head.append(img, title);
    card.append(head, el('p', 'tree-desc', u.description));
    if (rank > 0) {
      const total = u.modifiers.map((m) => describeModifier({ ...m, value: m.value * rank })).join(', ');
      card.append(el('div', 'tree-now', `Teraz: ${total}`));
    }
    const reasons: string[] = [];
    const points = branchPoints(this.content, profile, u.branch);
    if (points < u.requires) reasons.push(`wydaj ${u.requires} pkt w gałęzi ${BRANCH_NAMES[u.branch]} (masz ${points})`);
    if (u.after && !(profile.upgrades[u.after] ?? 0)) reasons.push(`najpierw: ${this.content.upgrades[u.after]?.name ?? u.after}`);
    if (reasons.length) card.append(el('div', 'tree-locked', `Zablokowane — ${reasons.join(', ')}`));
    const buy = el('button', 'btn primary tree-buy');
    if (cost === null) {
      buy.textContent = 'Maksymalna ranga';
      buy.disabled = true;
    } else {
      buy.append(el('span', '', 'Naucz się'), el('span', 'coin'), el('span', '', cost.toLocaleString('pl-PL')));
      buy.disabled = !unlocked || profile.gold < cost;
      if (unlocked && profile.gold < cost) buy.title = 'Za mało złota';
      buy.addEventListener('click', () => this.actions.buyUpgrade(u.id));
    }
    card.append(buy);
    side.append(card);
    return side;
  }

  /** Przywołanie: banery, zestaw tygodnia, gwarancje z paskami, sklepik za odłamki i szanse. */
  private summonPanel(profile: Profile): HTMLElement {
    const g = this.content.game.gacha;
    const now = Date.now();
    const cls = this.content.classes[profile.classId]!;
    const banner = this.content.banners[this.bannerId] ?? Object.values(this.content.banners)[0]!;
    const panel = this.panel('Przywołanie');

    const tabs = el('div', 'banner-tabs');
    for (const b of Object.values(this.content.banners)) {
      const t = el('button', b.id === banner.id ? 'banner-tab active' : 'banner-tab', b.name);
      t.addEventListener('click', () => {
        this.bannerId = b.id;
        sfx.click();
        if (this.profile) this.render(this.profile);
      });
      tabs.append(t);
    }

    const hero = el('div', `banner-hero banner-${banner.id}`);
    const bname = el('div', 'banner-name');
    bname.append(document.createTextNode(banner.name), glyph('info', 'info-ico'));
    this.textTip(bname, banner.name, banner.description, `Sprzęt dla klasy: ${cls.name}`);
    hero.append(bname);
    const featured = bannerFeatured(this.content, banner.id, now).filter((id) => this.content.items[id]?.classes.includes(profile.classId));
    if (featured.length > 0) {
      const row = el('div', 'featured-row');
      for (const id of featured) {
        const def = this.content.items[id]!;
        const rarity = banner.shop?.rarity ?? this.content.rarityOrder[this.content.rarityOrder.length - 2]!;
        const tile = el('div', 'featured-item');
        tile.append(this.icon({ uid: -1, itemId: id, rarity, level: 1 }), el('div', 'item-name', def.name));
        // Sklepik: wyróżniony przedmiot za odłamki.
        if (banner.shop) {
          const buy = el('button', 'btn small-btn', `◇ ${banner.shop.cost}`);
          buy.title = `Kup: ${def.name} (${this.content.rarities[banner.shop.rarity]!.name}) za ${banner.shop.cost} odłamków`;
          buy.disabled = profile.shards < banner.shop.cost;
          buy.addEventListener('click', () => this.actions.buyFeatured(banner.id, id));
          tile.append(buy);
        }
        row.append(tile);
      }
      const left = bannerRotationLeft(this.content, banner.id, now);
      hero.append(row);
      if (left !== null) hero.append(glyphChip('clock', formatDuration(left), 'rotation'));
    } else if (banner.rotation.length > 0) {
      hero.append(el('div', 'muted small', 'Brak zestawu dla tej klasy'));
    }

    const pulls = el('div', 'pulls');
    for (const n of [1, 10]) {
      const b = el('button', n === 10 ? 'btn primary' : 'btn', `Przywołaj ×${n}`);
      b.title = `Losuje sprzęt dla klasy: ${cls.name}`;
      b.append(el('small', '', ` ◆ ${(g.pullCost * n).toLocaleString('pl-PL')}`));
      b.disabled = !canPull(this.content, profile, n);
      b.addEventListener('click', () => this.actions.pull(n, banner.id));
      pulls.append(b);
    }

    // Gwarancje jako paski: ile losowań zostało do pewnego epickiego i legendarnego.
    const pity = el('div', 'pity');
    const bar = (label: string, done: number, total: number, color: string) => {
      const row = el('div', 'pity-row');
      const track = el('div', 'pity-track');
      const fill = el('div', 'pity-fill');
      fill.style.width = `${Math.min(100, (done / total) * 100)}%`;
      fill.style.background = color;
      track.append(fill);
      const lab = el('div', 'pity-label');
      const gem = el('span', 'pity-gem', '◆');
      gem.style.color = color;
      lab.append(gem, el('b', '', String(total - done)));
      row.append(lab, track);
      this.textTip(row, label, `Za ${total - done} przywołań na pewno.`);
      pity.append(row);
    };
    const order = this.content.rarityOrder;
    bar('Epicki gwarantowany', profile.pity.sinceEpic, g.pityEpic, this.content.rarities[order[order.length - 2]!]!.color);
    bar('Legendarny gwarantowany', profile.pity.sinceLegendary, g.pityLegendary, this.content.rarities[order[order.length - 1]!]!.color);

    const total = order.reduce((sum, id) => sum + this.content.rarities[id]!.weight, 0);
    const odds = el('div', 'odds');
    for (const id of order) {
      const r = this.content.rarities[id]!;
      const row = el('div', 'odds-row');
      const name = el('span', '', r.name);
      name.style.color = r.color;
      const shards = g.shardsPerDuplicate[id];
      row.append(name, el('span', 'muted small', shards ? `◇ ${shards}` : ''), el('b', '', `${((r.weight / total) * 100).toFixed(0)}%`));
      if (shards) this.textTip(row, r.name, `Duplikat na poziomie ${g.maxLevel} daje ◇ ${shards} odłamków.`);
      odds.append(row);
    }
    const body = el('div', 'panel-body');
    body.append(
      tabs,
      hero,
      pulls,
      el('h3', 'section-title', 'Gwarancje'),
      pity,
      el('h3', 'section-title', 'Szanse'),
      odds,
    );
    panel.append(body);
    return panel;
  }

  /** Osiągnięcia: wybór tytułu i ramki, lista z paskami postępu i odbiorem nagród. */
  private achievementsPanel(profile: Profile): HTMLElement {
    const all = Object.values(this.content.achievements);
    const done = all.filter((a) => profile.achievements[a.id]).length;
    const panel = this.panel('Osiągnięcia', `${done} / ${all.length}`);
    const body = el('div', 'panel-body');

    // Tytuły i ramki z odebranych nagród.
    const claimed = all.filter((a) => profile.achievements[a.id] === 'claimed');
    const chooser = (label: string, kind: 'title' | 'frame') => {
      const owned = claimed.filter((a) => (kind === 'title' ? a.reward.title : a.reward.frame));
      const row = el('div', 'cosmetic-row');
      row.append(el('span', 'cosmetic-label', label));
      const none = el('button', (kind === 'title' ? profile.title : profile.frame) === null ? 'chip active' : 'chip', 'Brak');
      none.addEventListener('click', () => (kind === 'title' ? this.actions.setTitle(null) : this.actions.setFrame(null)));
      row.append(none);
      for (const a of owned) {
        const active = (kind === 'title' ? profile.title : profile.frame) === a.id;
        const chip = el('button', active ? 'chip active' : 'chip', kind === 'title' ? a.reward.title! : a.reward.frame!.name);
        if (kind === 'frame') chip.style.setProperty('--frame', a.reward.frame!.color);
        if (kind === 'frame') chip.classList.add('frame-chip');
        chip.addEventListener('click', () => (kind === 'title' ? this.actions.setTitle(a.id) : this.actions.setFrame(a.id)));
        row.append(chip);
      }
      return row;
    };
    body.append(chooser('Tytuł', 'title'), chooser('Ramka', 'frame'));

    const list = el('div', 'achievement-list');
    // Najpierw do odebrania, potem w toku (od najbliższych), na końcu odebrane.
    const rank = (id: string) => (profile.achievements[id] === 'done' ? 0 : profile.achievements[id] === 'claimed' ? 2 : 1);
    const progress = new Map(all.map((a) => [a.id, achievementProgress(this.content, profile, a)]));
    const ratio = (id: string) => Math.min(1, progress.get(id)!.current / progress.get(id)!.target);
    const sorted = [...all].sort((x, y) => rank(x.id) - rank(y.id) || ratio(y.id) - ratio(x.id));
    for (const a of sorted) {
      const state = profile.achievements[a.id];
      const p = progress.get(a.id)!;
      const row = el('div', `achievement ${state ?? 'locked'}`);
      const text = el('div', 'ach-text');
      // Nazwa + nagrody jako symbole; opis zadania w podpowiedzi wiersza.
      const rewards = el('div', 'ach-reward');
      if (a.reward.currency) rewards.append(el('span', 'rw', `◆ ${a.reward.currency}`));
      if (a.reward.gold) {
        const g2 = el('span', 'rw');
        g2.append(el('span', 'coin'), document.createTextNode(String(a.reward.gold)));
        rewards.append(g2);
      }
      if (a.reward.title) rewards.append(glyphChip('star', a.reward.title, 'rw title'));
      if (a.reward.frame) {
        const f = el('span', 'rw frame-dot');
        f.style.setProperty('--frame', a.reward.frame.color);
        rewards.append(f);
      }
      const head = el('div', 'ach-head');
      head.append(el('div', 'ach-name', a.name), rewards);
      text.append(head);
      this.textTip(text, a.name, a.description, a.reward.frame ? `Ramka: ${a.reward.frame.name}` : '');
      const track = el('div', 'pity-track');
      const fill = el('div', 'pity-fill');
      fill.style.width = `${ratio(a.id) * 100}%`;
      track.append(fill);
      text.append(track);
      const side = el('div', 'ach-side');
      if (state === 'done') {
        const claim = el('button', 'btn primary small-btn', 'Odbierz');
        claim.addEventListener('click', () => this.actions.claimAchievement(a.id));
        side.append(claim);
      } else if (state === 'claimed') side.append(el('span', 'ach-check', '✓'));
      else side.append(el('span', 'muted small', `${Math.min(p.current, p.target).toLocaleString('pl-PL')} / ${p.target.toLocaleString('pl-PL')}`));
      row.append(text, side);
      list.append(row);
    }
    body.append(list);
    panel.append(body);
    return panel;
  }

  /**
   * Przywołanie z oprawą: portal w kolorze najlepszej rzadkości, potem karty odwracane po kolei
   * (każda z dźwiękiem zależnym od rzadkości). „Pokaż wszystkie” odwraca resztę od razu.
   */
  showResults(results: PullResult[]): void {
    const tierOf = (r: PullResult) => this.content.rarityOrder.indexOf(r.item.rarity);
    const best = results.reduce((a, r) => Math.max(a, tierOf(r)), 0);
    const bestColor = this.content.rarities[this.content.rarityOrder[best]!]!.color;

    const portal = el('div', `summon-portal tier-${best}`);
    portal.style.setProperty('--rarity', bestColor);
    portal.append(el('div', 'portal-ring'), el('div', 'portal-ring r2'), el('div', 'portal-core'));
    this.results.replaceChildren(el('h2', 'title-glow', 'Przywołanie…'), portal);
    this.results.hidden = false;
    sfx.summon();

    const timers: number[] = [];
    const grid = el('div', 'result-grid');
    const cards = results.map((r) => {
      const def = this.content.items[r.item.itemId]!;
      const rarity = this.content.rarities[r.item.rarity]!;
      const card = el('div', `flip-card tier-${tierOf(r)}`);
      card.style.setProperty('--rarity', rarity.color);
      const inner = el('div', 'flip-inner');
      const back = el('div', 'face back');
      back.append(el('div', 'back-emblem', '◆'));
      const front = el('div', 'face front');
      front.append(
        this.icon(r.item),
        el('div', 'item-name', def.name),
        el('div', 'item-meta', rarity.name),
        el('div', 'badge', r.shards !== undefined ? `◇ +${r.shards} odłamków` : r.upgraded ? `Poziom ↑ ${r.item.level}` : 'Nowy'),
      );
      if (r.featured) front.append(el('div', 'featured-badge', '★ Wyróżniony'));
      inner.append(back, front);
      card.append(inner);
      grid.append(card);
      return { card, tier: tierOf(r) };
    });

    const next = el('button', 'btn', 'Dalej');
    next.hidden = true;
    next.addEventListener('click', () => {
      timers.forEach(clearTimeout);
      this.results.hidden = true;
    });
    const skip = el('button', 'btn ghost', 'Pokaż wszystkie');
    const flip = (i: number) => {
      const c = cards[i]!;
      if (c.card.classList.contains('flipped')) return;
      c.card.classList.add('flipped');
      sfx.reveal(c.tier);
    };
    const finish = () => {
      cards.forEach((_, i) => cards[i]!.card.classList.add('flipped'));
      skip.hidden = true;
      next.hidden = false;
    };
    skip.addEventListener('click', () => {
      timers.forEach(clearTimeout);
      finish();
    });
    const buttons = el('div', 'buttons');
    buttons.append(skip, next);

    timers.push(
      window.setTimeout(() => {
        this.results.replaceChildren(el('h2', 'title-glow', 'Przywołanie'), grid, buttons);
        // Przy ×10 karty odwracają się szybciej, z krótką pauzą przed najlepszą.
        const gap = cards.length > 1 ? 130 : 230;
        let t = 250;
        cards.forEach((c, i) => {
          if (c.tier === best && best >= 2) t += 350;
          timers.push(window.setTimeout(() => flip(i), t));
          t += gap;
        });
        timers.push(window.setTimeout(finish, t + 300));
      }, 1100),
    );
  }
}

/** „3 dni 4 godz.” / „5 godz.” — czas do zmiany zestawu na banerze. */
function formatDuration(ms: number): string {
  const h = Math.floor(ms / 3_600_000);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d} d ${h % 24} godz.`;
  return `${Math.max(1, h)} godz.`;
}
